import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Country, Station } from '@vueling-ng/vy-cdk-searcher';
import { LogoutAuthOptions, OidcSecurityService } from 'angular-auth-oidc-client';
import { IAccountInfo } from 'app/services/api-services/accountManager/models/account-info';
import { EmptySession, SessionState } from 'app/services/external/auth-oidc/models/session.model';
import { AlertBanner } from 'app/shared/alerts/alert-banner/models/alert-banner.model';
import { ImageLayoutError } from 'app/shared/state/models/app-state.model';
import { StateManagerService } from 'app/shared/state/state-manager.service';
import { RoleToUserType } from 'app/views/auth/model/types-roles-code.config';
import { environment } from 'environments/environment';
import { catchError, Observable, of, take, tap, throwError } from 'rxjs';
import { OidcLoginResponse, OidcLoginResponseStubby } from './models/authOidc.interface';

@Injectable({
  providedIn: 'root'
})
export class SessionOidcService {
  private readonly _oidcSecurityService = inject(OidcSecurityService);
  private readonly _stateManagerService = inject(StateManagerService);

  private readonly _sessionOidc = signal<OidcLoginResponse>(OidcLoginResponse);
  public readonly currentSessionOidc = this._sessionOidc.asReadonly();

  private readonly _session = signal<SessionState>(EmptySession);
  public readonly currentSession = this._session.asReadonly();

  public checkCurrentSession(): Observable<OidcLoginResponse> {
    if (!environment.sso.enabledSSO) {
      this.updateOidcSession(OidcLoginResponseStubby);
      return of(OidcLoginResponseStubby);
    }
    return this._oidcSecurityService.checkAuth().pipe(
      take(1),
      tap((auth: OidcLoginResponse) => {
        this.updateOidcSession(auth);
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }

  public authorize(): void {
    this._oidcSecurityService.authorize();
  }

  public updateState(
    countries: Country[],
    stations: Station[],
    imageLayout: string,
    globalAlerts: AlertBanner[],
    userInfo?: IAccountInfo
  ): void {
    this._stateManagerService.update((draft) => {
      draft.countries = countries;
      draft.stations = stations;
      draft.imageLayout = imageLayout;
      draft.globalAlerts = globalAlerts;
      if (userInfo) {
        draft.user = {
          email: userInfo.email,
          userName: userInfo.name,
          userType: RoleToUserType[this._sessionOidc().userData.roleCode] ?? ''
        };
      }
    });
  }

  public updateCurrentSession(userInfo: IAccountInfo): void {
    const currentSession: SessionState = {
      connection: { ssoStatus: 'Established', status: 'Established' },
      accessToken: this._sessionOidc().accessToken,
      duration: this._sessionOidc().expires_in,
      expiresAt: this._sessionOidc().expires_in,
      partnerInformation: { emailAddress: userInfo.email, externalNote: userInfo.externalNote, name: userInfo.name },
      tokenType: 'Bearer',
      userType: RoleToUserType[this._sessionOidc().userData.roleCode]
    };
    this._session.set(currentSession);
  }

  public logoff(): Observable<unknown> {
    this.updateOidcSession(OidcLoginResponse);
    this.updateState([], [], ImageLayoutError, []);
    const currentLang: LogoutAuthOptions = {
      customParams: {
        ui_locales:
          this._stateManagerService.currentState.language?.split('-')[0] ??
          environment.internationalization.defaultLanguage
      }
    };
    return this._oidcSecurityService.logoff('', currentLang);
  }

  private updateOidcSession(newSession: OidcLoginResponse): void {
    this._sessionOidc.set(newSession);
    const infoUser: IAccountInfo = {
      name: '',
      email: '',
      address: { city: '', countryCode: '', postalCode: '', provinceState: '', lineOne: '' },
      phoneNumbers: [],
      internalNote: '',
      externalNote: '',
      integrationId: this._sessionOidc().userData.organizationCode ?? ''
    };
    this.updateCurrentSession(infoUser);
  }
}
